// covered-by: frontends/carrie/src/ask-v2/project/__tests__/AskV2ConflictCard.test.tsx
import { type AskPunttOpenItemResolutionOption } from "@mg/schemas/src/christo/askPunttOpenItems";
import { Button, Text, TextArea } from "@radix-ui/themes";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { resolveChatOpenItem, resolveOpenItem } from "./openItemsApi";
import { projectOpenItemsKey } from "./useProjectOpenItems";

import { ASK_V2_FOR_YOU_QUERY_KEY } from "../home/useAskV2ForYou";

function useOptionLabel(
  ruleReview: boolean,
): (option: AskPunttOpenItemResolutionOption) => string {
  const { t } = useTranslation("ui");
  // A review of a Knowledge Database rule reads "Keep the rule" / "Agree" (decision 16).
  if (ruleReview) {
    return (option) =>
      option.resolution === "keep"
        ? t("askV2.conflictCard.keepRule")
        : t("askV2.conflictCard.agree");
  }
  return (option) =>
    option.resolution === "keep"
      ? t("askV2.conflictCard.keep", { answer: option.answer })
      : t("askV2.conflictCard.accept", { answer: option.answer });
}

/**
 * A resolution request answered by choosing, not typing (L2.20, decision d23).
 * Anyone the request reaches sees its options; only an Expert acts on them
 * (`canAct`). Acting: one button per option warhol filed with the request --
 * keep the standing answer, or accept the proposal -- and a reason box;
 * accepting changes the decision, so it needs a reason. The click sends the
 * typed choice; warhol's decision door applies it and checks the role.
 */
export function AskV2ConflictCard({
  projectId,
  itemId,
  options,
  canAct,
  reasonRequired = true,
  ruleReview = false,
}: {
  /** Null for a request sent from a chat outside any project. */
  projectId: string | null;
  itemId: string;
  options: AskPunttOpenItemResolutionOption[];
  canAct: boolean;
  /** False for a fact request: its reason is optional (decision 12); accepting a proposal still needs one. */
  reasonRequired?: boolean;
  /** A review of a Knowledge Database rule: Keep the rule / Agree, and Update shown disabled until L2.26. */
  ruleReview?: boolean;
}) {
  const { t } = useTranslation("ui");
  const optionLabel = useOptionLabel(ruleReview);
  const titleId = `conflict-card-title-${itemId}`;
  return (
    <div
      role="group"
      aria-labelledby={titleId}
      data-testid={`conflict-card-${itemId}`}
      className="mt-1 flex flex-col gap-2 rounded-lg border border-puntt-neutral-gray-6 p-2"
    >
      <Text
        as="p"
        size="1"
        weight="medium"
        id={titleId}
        className="text-puntt-neutral-gray-12"
      >
        {t("askV2.conflictCard.title")}
      </Text>
      {canAct ? (
        <ConflictCardChoice
          projectId={projectId}
          itemId={itemId}
          options={options}
          reasonRequired={reasonRequired}
          ruleReview={ruleReview}
        />
      ) : (
        // why: no "Your answer" label here -- the viewer is not the one who answers.
        <ul className="flex flex-col gap-1">
          {options.map((option, index) => (
            <li
              key={option.resolution}
              data-testid={`conflict-card-choice-${itemId}-${index}`}
              className="list-none text-[12px] leading-snug text-puntt-neutral-gray-11"
            >
              {optionLabel(option)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ConflictCardChoice({
  projectId,
  itemId,
  options,
  reasonRequired,
  ruleReview,
}: {
  projectId: string | null;
  itemId: string;
  options: AskPunttOpenItemResolutionOption[];
  reasonRequired: boolean;
  ruleReview: boolean;
}) {
  const { t } = useTranslation("ui");
  const optionLabel = useOptionLabel(ruleReview);
  const updateLaterId = `conflict-card-update-later-${itemId}`;
  const queryClient = useQueryClient();
  const [chosen, setChosen] = useState<"keep" | "accept" | null>(null);
  const [reason, setReason] = useState("");
  const resolve = useMutation({
    mutationFn: (resolution: "keep" | "accept") =>
      projectId
        ? resolveOpenItem(projectId, itemId, resolution, reason.trim())
        : resolveChatOpenItem(itemId, resolution, reason.trim()),
    onSuccess: () => {
      if (projectId) {
        void queryClient.invalidateQueries({
          queryKey: projectOpenItemsKey(projectId),
        });
      }
      void queryClient.invalidateQueries({
        queryKey: ASK_V2_FOR_YOU_QUERY_KEY,
      });
    },
  });
  const reasonId = `conflict-card-reason-${itemId}`;
  const needsReason =
    reasonRequired && chosen === "accept" && reason.trim().length === 0;
  const canSave = chosen !== null && !needsReason && !resolve.isPending;
  return (
    <>
      <div
        role="group"
        aria-label={t("askV2.conflictCard.choices")}
        className="flex flex-wrap gap-2"
      >
        {options.map((option, index) => {
          const selected = chosen === option.resolution;
          return (
            <Button
              key={option.resolution}
              type="button"
              size="1"
              variant={selected ? "solid" : "soft"}
              aria-pressed={selected}
              data-testid={`conflict-card-option-${itemId}-${index}`}
              onClick={() => setChosen(option.resolution)}
            >
              {optionLabel(option)}
            </Button>
          );
        })}
        {ruleReview ? (
          // why disabled and shown: archiving the rule comes with L2.26 (decision 16); the Expert sees it is coming.
          <Button
            type="button"
            size="1"
            variant="soft"
            disabled
            aria-describedby={updateLaterId}
            data-testid={`conflict-card-update-rule-${itemId}`}
          >
            {t("askV2.conflictCard.updateRule")}
          </Button>
        ) : null}
      </div>
      {ruleReview ? (
        <Text
          as="p"
          size="1"
          id={updateLaterId}
          data-testid={updateLaterId}
          className="text-puntt-neutral-gray-11"
        >
          {t("askV2.conflictCard.updateRuleLater")}
        </Text>
      ) : null}
      <label
        htmlFor={reasonId}
        className="text-[12px] leading-snug text-puntt-neutral-gray-11"
      >
        {reasonRequired
          ? t("askV2.conflictCard.reasonLabel")
          : t("askV2.conflictCard.reasonLabelOptional")}
      </label>
      <TextArea
        id={reasonId}
        size="1"
        data-testid={`conflict-card-reason-${itemId}`}
        placeholder={t("askV2.conflictCard.reasonPlaceholder")}
        value={reason}
        maxLength={600}
        onChange={(event) => setReason(event.target.value)}
      />
      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="1"
          data-testid={`conflict-card-submit-${itemId}`}
          disabled={!canSave}
          onClick={chosen ? () => resolve.mutate(chosen) : undefined}
        >
          {resolve.isPending
            ? t("askV2.conflictCard.saving")
            : t("askV2.conflictCard.save")}
        </Button>
        {resolve.isSuccess ? (
          <Text
            as="p"
            size="1"
            role="status"
            data-testid={`conflict-card-result-${itemId}`}
            className="text-puntt-neutral-gray-11"
          >
            {resolve.data.message}
          </Text>
        ) : null}
        {resolve.isError ? (
          <Text
            as="p"
            size="1"
            color="red"
            role="alert"
            data-testid={`conflict-card-error-${itemId}`}
          >
            {t("askV2.conflictCard.failed")}
          </Text>
        ) : null}
      </div>
    </>
  );
}
